import { skipToken } from '@reduxjs/toolkit/query';
import { isDefined, isNotDefined } from '@sgme/fp';
import { Button, VStack } from '@sgme/ui';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import { selectCurrencyPairPrecision, selectIsCurrencyPairInverted } from '@/api/config/selectors.ts';
import { useContactsQuery } from '@/api/contacts/index.ts';
import { useRecordPriceMutation } from '@/api/mifid/index.ts';
import { CashTileFooter } from '@/App/tiles/CashTile/shared/CashTileFooter.tsx';
import type { RequestCashRfsParams } from '@/store/cash/api/requestCashRfs.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import { selectCashTile } from '@/store/cash/selectors/tile.ts';
import type { FxCash } from '@/store/cash/state.ts';
import { useAppSelector } from '@/store/hooks.ts';
import { getSignalRConnectionId } from '@/store/streaming/slice';
import { selectTileClientId } from '@/store/tiles/selectors.ts';

export type ForwardFooterProps = {
  tileId: string;
};

export const ForwardFooter = (props: ForwardFooterProps) => {
  const { tileId } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();

  const { formatDate, formatTime } = useIntl();

  const {
    isVisible,
    currencyPair,
    rfsRequestParams,
    rfsId,
    isRfsPending,
    isPriceable,
    canRecordRates,
    rfsRemainingTime,
    totalTime,
    traderName,
    quoteId,
    rates,
    negotiations,
    rfsStartDate,
  } = useAppSelector((state) => {
    const tile = selectCashTile(state, tileId);

    if (isNotDefined(tile)) {
      return {
        isVisible: false,
        isRfsPending: false,
        isPriceable: false,
        canRecordRates: false,
      };
    }

    const isPriceable = tile.tradeCapture.status === 'CHECKED' && tile.tradeCapture.isReadyToPrice;

    const signalRConnectionId = getSignalRConnectionId(state);
    const currencyPairPrecision = selectCurrencyPairPrecision(state, tile.properties.currencyPair);

    const isCurrencyPairInverted = selectIsCurrencyPairInverted(state, tile?.properties?.currencyPair);

    if (tile.type !== 'FxFwd' && tile.type !== 'FxNdf' && tile.type !== 'FxSpot' && isCurrencyPairInverted) {
      return {
        isVisible: false,
        isRfsPending: false,
        isPriceable,
        canRecordRates: false,
      };
    }

    const clientId = selectTileClientId(state, tileId);

    const rfsRequestParams = getRfsRequestParams(
      tileId,
      tile,
      signalRConnectionId,
      isCurrencyPairInverted,
      currencyPairPrecision,
      clientId,
    );

    const quote = tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote : undefined;

    const priceStatus = selectCashPriceStatus(state, tileId);

    return {
      isVisible:
        priceStatus?.status === 'idle' ||
        priceStatus?.status === 'rfs-not-ready' ||
        priceStatus?.status === 'rfs-required' ||
        priceStatus?.status === 'rfs-pending' ||
        priceStatus?.status === 'rfs-price',

      currencyPair: tile.properties.currencyPair,
      rfsRequestParams,

      rfsId: tile.properties.rfs?.rfsId,
      isRfsPending: priceStatus?.status === 'rfs-pending',
      isPriceable,
      canRecordRates: true,
      rfsRemainingTime: quote?.rfsRemainingTime,
      totalTime: quote?.rfsWindow,
      traderName: quote?.traderId,
      quoteId: tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.quote.quoteId : undefined,
      rates: isDefined(priceStatus) && priceStatus.status === 'rfs-price' ? priceStatus.rates : undefined,
      negotiations: tile.properties.rfs?.status === 'STREAMED' ? [...tile.properties.rfs.negotiations].reverse() : [],
      rfsStartDate: tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs.requestedAt : undefined,
    };
  }, deepEqual);

  const [recordPrice] = useRecordPriceMutation();

  useContactsQuery(
    isDefined(traderName)
      ? {
          search: traderName,
          fields: ['jobType', 'jobTitle', 'emails', 'phones', 'internalHrTeam'],
        }
      : skipToken,
  );

  const handleRecordRates = useCallback(() => {
    if (isNotDefined(rfsId) || isNotDefined(rates) || isNotDefined(quoteId)) {
      return;
    }

    sgwtWebAnalytics?.trackEvent('Cash', 'RFS price recorded');

    recordPrice({
      tileId,
      date: Date.now(),

      quoteId,
      rfsId,
      prices: [
        {
          bid: rates.bid,
          ask: rates.ask,
        },
      ],
    });
  }, [tileId, rfsId, rates, quoteId, recordPrice, sgwtWebAnalytics]);

  return (
    <CashTileFooter
      tileId={tileId}
      isVisible={isVisible}
      isPriceable={isPriceable}
      rfsRequestParams={rfsRequestParams}
      rfsId={rfsId}
      isRfsPending={isRfsPending}
      remainingTime={rfsRemainingTime}
      totalTime={totalTime}
      traderName={traderName}
      additionalControls={
        <>
          <Button
            iconPosition="start"
            purpose="primary"
            color="danger"
            size="sm"
            onClick={handleRecordRates}
            disabled={!canRecordRates}
            data-e2e="timestamp-button"
          >
            <i className="icon">timer</i>
            Timestamp
          </Button>

          {isDefined(rfsStartDate) && isDefined(negotiations) && (
            <OverlayTrigger
              overlay={
                <Popover style={{ maxWidth: '300px' }}>
                  <Popover.Header data-e2e="timestamp-popover-title">
                    <FormattedMessage id="cash.forward.negociation.popover.header" values={{ currencyPair }} />
                  </Popover.Header>

                  <Popover.Body>
                    <VStack className="mb-8px">
                      <div className="mb-8px">
                        <FormattedMessage
                          id="cash.forward.negociation.popover.initialisation"
                          values={{
                            initialisationDate: `${formatDate(rfsStartDate, { dateStyle: 'long' })} ${formatTime(rfsStartDate, { timeStyle: 'short' })}`,
                          }}
                        />
                      </div>

                      <div data-e2e="timestamp-popover-body">
                        {negotiations.map((negotiation, negotiationIndex) => (
                          // oxlint-disable-next-line no-array-index-key -- the index is really the key
                          <div key={negotiationIndex} className="text-nowrap">
                            <FormattedMessage
                              id="cash.forward.negociation.popover.item"
                              values={{
                                index: negotiations.length - negotiationIndex,
                                date: `${formatDate(negotiation.date, { dateStyle: 'medium' })} ${formatTime(negotiation.date, { timeStyle: 'short' })}`,
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </VStack>
                  </Popover.Body>
                </Popover>
              }
              placement="right"
              trigger="click"
            >
              <span data-e2e="timestamp-count" className="border badge rounded-pill badge-discreet-secondary fs-12">
                {negotiations?.length ?? 0}
              </span>
            </OverlayTrigger>
          )}
        </>
      }
    />
  );
};

const getRfsRequestParams = (
  tileId: string,
  tile: FxCash,
  signalRConnectionId: string | undefined,
  isCurrencyPairInverted: boolean,
  currencyPairPrecision: number,
  clientId: number | undefined,
): RequestCashRfsParams | undefined => {
  const isPriceable = tile.tradeCapture.status === 'CHECKED' && tile.tradeCapture.isReadyToPrice;

  if (
    !isPriceable ||
    isNotDefined(tile.properties.notional) ||
    isNotDefined(tile.properties.notionalCurrencyIndex) ||
    isNotDefined(tile.properties.currency1) ||
    isNotDefined(tile.properties.currency2) ||
    (tile.type === 'FxFwd' && isNotDefined(tile.properties.isSpotWithInvertedCurrencyPair)) ||
    isNotDefined(signalRConnectionId) ||
    isNotDefined(clientId)
  ) {
    return undefined;
  }

  if (tile.type === 'FxFwd' || tile.type === 'FxSpot') {
    return {
      tileId,
      productName: 'FxFwd',
      signalRConnectionId,
      clientId,

      currency1: tile.properties.currency1,
      currency2: tile.properties.currency2,
      isCurrencyPairInverted,

      notional: tile.properties.notional,
      notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
      maturityDate: `${tile.properties.maturityDate}T00:00:00.000Z`,
      currencyPairPrecision,
    };
  }

  if (
    isNotDefined(tile.properties.fixingCurrency) ||
    isNotDefined(tile.properties.fixingDate) ||
    isNotDefined(tile.properties.fixingSource)
  ) {
    return undefined;
  }

  if (
    tile.properties.isCrossed &&
    (isNotDefined(tile.properties.xCurrency) || isNotDefined(tile.properties.sndFixingSource))
  ) {
    return undefined;
  }

  return {
    tileId,
    productName: 'FxNdf',
    signalRConnectionId,
    clientId,

    currency1: tile.properties.currency1,
    currency2: tile.properties.currency2,
    notional: tile.properties.notional,
    notionalCurrencyIndex: tile.properties.notionalCurrencyIndex,
    maturityDate: `${tile.properties.maturityDate}T00:00:00.000Z`,
    currencyPairPrecision,

    fixingDate: tile.properties.fixingDate,
    fixingCurrency: tile.properties.fixingCurrency,
    fixingSource: tile.properties.fixingSource,

    ...(tile.properties.isCrossed
      ? {
          sndFixingSource: tile.properties.sndFixingSource,
          xCurrency: tile.properties.xCurrency,
        }
      : {}),
  };
};
